import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DriverManagementService } from '@app-driver-management/services/driver-management.service';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { DriverEnrollmentComponent } from '../driver-enrollment/driver-enrollment.component';
import { DriverImageUploadService } from '@app-driver-management/services/driver-image-upload.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { IMG_PLACEHOLDER_URL, QR_CODE_IMAGE_HEIGHT, QR_CODE_IMAGE_WIDTH } from '@app-driver-management/common/driver-management.constants';
import { DataService } from '@app-core/services/data/data.service';
import { DeleteDriverResponse, QrCodeData } from '@app-driver-management/common/driver-management.model';
import jsPDF from 'jspdf';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { CLIENT_CONFIG } from '@config/config';
import { DriverQrcodeInfoComponent } from '../driver-qrcode-info/driver-qrcode-info.component';

@Component({
  selector: 'app-driver-images',
  templateUrl: './driver-images.component.html',
  styleUrls: ['./driver-images.component.scss'],
})
export class DriverImagesComponent implements OnInit, OnDestroy {
  @ViewChild('imageUploadMessage', { static: false }) imageUploadMessage: ElementRef<HTMLInputElement>;
  @ViewChild('imageUpload') imageUpload!: ElementRef;
  public driverImages = [];
  public loader = true;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public imageUrl = IMG_PLACEHOLDER_URL;
  public repeatCount = 5;
  public images: string | ArrayBuffer | null = null;

  //   driver enrollment
  public invalidSampleIncides = [];
  public showError;
  public uploadedFiles = [];
  public totalUploadCount = 5;
  public isReplaceMode;
  public showWarning;
  public validSamples = [];
  public invalidSamples = [];
  public showLoader;
  public showSuccess;
  public conflictingPersonId;
  public conflictingDriverId;
  public conflictPersonError;
  public conflictSamples = [];
  public generatedSampleIds = [];
  public duplicatePersonError;
  public showDriverImageLoader = false;
  public driverDetails;

  public qrCodeData: string | null = null;
  public selectedDriverName: string = '';
  public selectedDriverId: string = '';
  public logoUrl: string;
  public logoImage: string;
  public logo: string;
  public config = CLIENT_CONFIG;
  public qrCodeDownloadError: string | null = null;
  public showDriverImageIcon: boolean;
  public isQRCodeEnabled: boolean;
  public showFRdetails: boolean;
  public isAddDriverImagesEnabled: boolean;

  constructor(
    public dialogRef: MatDialogRef<DriverImagesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private driverManagementService: DriverManagementService,
    private dialog: MatDialog,
    private imageHandlerService: DriverImageUploadService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    public dataService: DataService,
    public durationRangeService: DurationRangeService
  ) {}

  public ngOnInit() {
    this.showDriverImageIcon = this.data.showDriverImageIcon;
    this.isQRCodeEnabled = this.data.isQRCodeEnabled;
    this.isAddDriverImagesEnabled = this.data.addDriverImages;
    this.showFRdetails = this.data.driver.appMetadata?.personId || false;
    this.driverDetails = this.data.driver;
    if (this.showFRdetails) {
      const params = {
        personId: this.data.driver.appMetadata.personId,
      };
      this.showDriverImageLoader = true;
      this.driverManagementService
        .getDriverImages(params)
        .pipe(
          finalize(() => {
            this.showDriverImageLoader = false;
          }),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(
          (res) => {
            this.driverImages = res.samples;
          },
          () => {
            this.driverImages = [];
          }
        );
    }

    this.viewQRCode(this.data.driver.driverId, this.data.driver.driverName);
    const {
      logo: { lightLogo },
    } = this.config;
    const logoUrl = lightLogo;
    this.convertImageToBase64(logoUrl).then((base64) => {
      this.logo = base64;
    });
  }

  ngAfterViewInit(): void {
    if (this.isAddDriverImagesEnabled) {
      setTimeout(() => {
        const el = this.imageUploadMessage?.nativeElement;
        if (el) {
          el.scrollIntoView({ block: 'end', inline: 'nearest' });
          el.focus({ preventScroll: true });
        }
      }, 300); // longer delay to ensure layout is settled
    }

    if (this.isAddDriverImagesEnabled) {
      // Delay is necessary to ensure the element is available in DOM
      setTimeout(() => {
        this.imageUpload.nativeElement.click();
      }, 0);
    }
  }

  public viewGuide(): void {
    this.dialog.open(DriverEnrollmentComponent, {
      panelClass: ['incident-modal', 'mobile-modal'],
      maxWidth: '640px',
      autoFocus: false,
      position: { top: '24px' },
    });
  }

  public viewGuideQrcode(): void {
    this.dialog.open(DriverQrcodeInfoComponent, {
      panelClass: ['incident-modal', 'mobile-modal'],
      maxWidth: '640px',
      autoFocus: false,
      position: { top: '24px' },
    });
  }

  public onClose() {
    this.dialogRef.close();
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.unsubscribe();
  }
  public async chooseImages(event): Promise<void> {
    if (!this.invalidSampleIncides.length) {
      this.driverImages = [];
    }
    this.showError = false;
    this.uploadedFiles = event.target.files;

    const validation = this.imageHandlerService.validateFiles(this.uploadedFiles, this.totalUploadCount);
    if (!validation.isValid) {
      if (!this.isReplaceMode) {
        this.resetDriverEnrollment();
      }
      this.showWarning = validation.warning;
      return;
    }

    this.showWarning = false;

    try {
      const dataUrls = await this.imageHandlerService.readFilesAsDataURL(this.uploadedFiles);

      // Step 3: Process images
      this.driverImages = this.imageHandlerService.processImages(
        dataUrls,
        this.isReplaceMode,
        this.invalidSampleIncides,
        this.driverImages
      );
      if (this.driverImages.length === 5) {
        this.addDriverImages();
      }
    } catch (error) {
      console.error('Error reading files:', error);
      this.showError = true;
    }
  }
  private resetDriverEnrollment(): void {
    this.totalUploadCount = 5;
    this.validSamples = [];
    this.invalidSampleIncides = [];
    this.invalidSamples = [];
    this.showLoader = false;
    this.loader = false;
    this.showWarning = false;
    this.showSuccess = false;
    this.isReplaceMode = false;
    this.conflictingPersonId = '';
    this.conflictingDriverId = '';
    this.conflictPersonError = false;
    this.conflictSamples = [];
    this.driverImages = [];
    this.uploadedFiles = [];
    this.generatedSampleIds = [];
  }
  public uploadFilesToS3(sampleIdsList, fileList): void {
    this.showLoader = true;
    this.loader = true;

    this.imageHandlerService.uploadFilesToS3(sampleIdsList, fileList).subscribe(
      () => {
        this.enrollDriver(sampleIdsList);
      },
      () => {
        this.loader = false;
        this.showLoader = false;
        this.showError = true;
      }
    );
  }
  private addDriverImages(): void {
    const fileList = Object.keys(this.uploadedFiles).map((key) => this.uploadedFiles[key]);

    this.showLoader = true;
    this.driverManagementService.generateSampleUrls().subscribe(
      ({ samples = [] }) => {
        if (this.isReplaceMode) {
          this.generatedSampleIds = this.generatedSampleIds.map((x, idx) => {
            const invalidSampleIds = this.invalidSamples.map((y) => y.sampleId);
            if (invalidSampleIds.includes(x)) {
              return samples[idx].sampleId;
            }
            return x;
          });
        } else {
          this.generatedSampleIds = samples.map((x) => x.sampleId);
        }
        const sampleList = this.isReplaceMode ? samples.slice(0, this.totalUploadCount) : samples;
        this.uploadFilesToS3(sampleList, fileList);
        this.driverImages = this.driverImages.map((x, idx) => {
          return {
            ...x,
            sampleId: this.generatedSampleIds[idx],
          };
        });
      },
      () => {
        this.loader = false;
        this.showLoader = false;
        this.showError = true;
      }
    );
  }
  private enrollDriver(sampleList): void {
    this.showLoader = true;
    const driverId = this.data.driver.driverId;
    const driverName = this.data.driver?.driverName;
    const personId = this.data.driver?.appMetadata?.personId;
    const driverEmail = this.data.driver?.email;
    const body = {
      sampleIds: sampleList.map((x) => x.sampleId),
      metadata: {
        driverId,
        driverName,
        driverEmail,
      },
      personId,
      ...(this.isReplaceMode ? { validSampleIds: this.validSamples.length ? this.validSamples.map((x) => x.sampleId) : undefined } : {}),
    };

    this.invalidSampleIncides = [];
    this.driverManagementService.enrollDriver(body).subscribe(
      (res) => {
        this.invalidSamples = [];
        this.driverImages = this.driverImages.map((img) => {
          return {
            ...img,
            isValid: true,
          };
        });

        if (personId) {
          this.showSuccess = true;
          this.dialogRef.close(true);
        }
        // Update driver call
        if (res && !personId) {
          this.updateDriverMetadataWithPersonId(res.body.personId).subscribe(
            () => {
              this.showSuccess = true;
              this.dialogRef.close(true);
            },
            () => {
              this.showError = true;
              this.resetDriverEnrollment();
              this.deletePersonDetails([res.body.personId || personId]).subscribe();
            }
          );
        }
        this.showLoader = false;
        this.loader = false;
      },
      (err) => {
        const data = err.error;
        if (err && err.status === 409) {
          this.verifyConflictPersonDetails(data);
          return;
        }

        this.isReplaceMode = true;
        this.invalidSampleIncides = [];
        if (err && err.status === 400) {
          this.validSamples = data.validSamples?.map((sample) => {
            return {
              sampleId: sample,
              isValid: true,
            };
          });
          this.invalidSamples = data.invalidSamples?.map((sample) => {
            return {
              ...sample,
              isValid: false,
            };
          });

          const allSamples = [...this.validSamples, ...this.invalidSamples];

          // assigning valid state for each image
          this.driverImages?.map((img, index) => {
            allSamples.map((sample) => {
              if (img.sampleId === sample.sampleId) {
                this.driverImages[index].isValid = sample.isValid;
              }
            });
          });

          // getting invalid image indices
          this.driverImages.map((img, index) => {
            if (img.isValid === false) {
              this.invalidSampleIncides.push(index);
            }
          });

          this.totalUploadCount = this.invalidSamples.length;
          this.showLoader = false;
          this.loader = false;
          return;
        }
        this.loader = false;
        this.showLoader = false;
        this.showError = true;
        this.duplicatePersonError = false;
        this.resetDriverEnrollment();
      }
    );
  }
  private updateDriverMetadataWithPersonId(personId: string): Observable<any> {
    const driverId = this.data.driver.driverId;
    const email = this.data.driver?.email;
    const tagIds = this.data.driver?.driverTags?.map((tag) => tag.tagId);
    const updateDriverBody = {
      driverId: driverId,
      driverName: this.data.driver?.driverName,
      username: email?.length > 0 ? email : driverId,
      ...(tagIds && tagIds.length > 0 ? { tagIds: tagIds } : {}),
      ...(email ? { email: email } : {}),
      personId: personId,
      source: this.data.driver?.appMetadata?.personId || 'REGISTERED_DRIVER',
    };
    this.showSuccess = false;
    this.showLoader = true;
    return this.driverManagementService.editDriver(updateDriverBody).pipe(
      finalize(() => {
        this.showLoader = false;
      })
    );
  }
  private deletePersonDetails(personIds): Observable<DeleteDriverResponse> {
    const param = {
      personIds,
    };
    return this.driverManagementService.deletePersonDetails(param);
  }
  private verifyConflictPersonDetails(data: { message: string }): void {
    // data.message format: "Person is already enrolled with personId- dbb4f03c-44e7-4c29-92f4-897342f0a60b"
    this.showLoader = true;
    const [, conflictingPersonId] = (data.message || '').split('- '); // space is required after hyphen
    this.conflictingPersonId = conflictingPersonId;
    if (!conflictingPersonId) {
      this.duplicatePersonError = true;
      this.resetDriverEnrollment();
      return;
    }
    const params = { personId: conflictingPersonId };
    this.driverManagementService
      .getDriverImages(params)
      .pipe(
        finalize(() => {
          this.showLoader = false;
          this.loader = false;
        })
      )
      .subscribe(
        (personDetails) => {
          const { metadata = {}, samples = [] } = personDetails;
          this.showError = true;
          if (this.data.driver.driverId === metadata.driverId) {
            this.conflictPersonError = true;
            this.conflictSamples = samples;
            this.duplicatePersonError = true;
            return;
          }
          this.duplicatePersonError = true;
          this.resetDriverEnrollment();
          this.conflictingDriverId = metadata?.driverId;
        },
        () => {
          this.showError = true;
          this.duplicatePersonError = true;
          this.resetDriverEnrollment();
        }
      );
  }
  public onAcceptConflict(): void {
    this.updateDriverMetadataWithPersonId(this.conflictingPersonId).subscribe(
      () => {
        this.snackBarService.success(this.translate.instant('driverEnrollmentSuccessfullyEnolled'));
        this.showSuccess = true;
        this.showError = false;
        this.loader = false;
      },
      () => {
        this.showError = true;
        this.loader = false;
        this.resetDriverEnrollment();
        this.deletePersonDetails([this.conflictingPersonId]).subscribe();
      }
    );
  }

  public onRejectConflict(): void {
    this.showLoader = true;
    this.deletePersonDetails([this.conflictingPersonId])
      .pipe(finalize(() => (this.showLoader = false)))
      .subscribe(
        () => {
          this.loader = false;
          this.showError = false;
          this.resetDriverEnrollment();
        },
        () => {
          this.showError = true;
          this.loader = false;
          this.resetDriverEnrollment();
        }
      );
  }

  public downloadQRcode(driverId: string, driverName: string) {
    this.qrCodeDownloadError = null; // Reset error before starting
    this.driverManagementService
      .getGenerateQRCode(driverId)
      .pipe(
        finalize(() => {}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: QrCodeData) => {
          try {
            const { data } = res;
            let QRCodeBase64 = String(data.qrCode);
            const base64Image = QRCodeBase64.startsWith('data:image') ? QRCodeBase64 : `data:image/png;base64,${QRCodeBase64}`;

            const doc = new jsPDF();
            const imgWidth = QR_CODE_IMAGE_WIDTH;
            const imgHeight = QR_CODE_IMAGE_HEIGHT;
            const x = (210 - imgWidth) / 2;

            doc.addImage(base64Image, 'PNG', x, 20, imgWidth, imgHeight);

            const driverIdLabel = this.translate.instant('DRIVERID');
            const driverNameLabel = this.translate.instant('DRIVERNAME');
            doc.setFontSize(14);
            doc.text(`${driverIdLabel}:`, 10, 230, { align: 'left' });
            doc.setFont('helvetica', 'bold');
            doc.text(`${driverId || '-'}`, 10, 240, { align: 'left' });

            doc.setFont('helvetica', 'normal');
            doc.text(`${driverNameLabel}:`, 10, 250, { align: 'left' });
            doc.setFont('helvetica', 'bold');
            doc.text(`${driverName || '-'}`, 10, 260, { align: 'left' });

            const base64logo = this.logo.startsWith('data:image') ? this.logo : `data:image/png;base64,${this.logo}`;
            doc.addImage(base64logo, 'PNG', 150, 260, 50, 30);

            doc.save(`driver-${driverId}-QRCode.pdf`);
          } catch (e) {
            this.qrCodeDownloadError = 'Failed to generate PDF.';
          }
        },
        () => {
          this.qrCodeDownloadError = 'QR code download failed. Please try again.';
        }
      );
  }

  public viewQRCode(driverId: string, driverName: string) {
    this.driverManagementService
      .getGenerateQRCode(driverId)
      .pipe(
        finalize(() => {}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((res: QrCodeData) => {
        const { data } = res;
        const QRCodeBase64 = String(data.qrCode);
        this.qrCodeData = QRCodeBase64.startsWith('data:image') ? QRCodeBase64 : `data:image/png;base64,${QRCodeBase64}`;
        this.selectedDriverName = driverName;
        this.selectedDriverId = driverId;
        this.logoImage = this.logo.startsWith('data:image') ? this.logo : `data:image/png;base64,${this.logo}`;
      });
  }

  private convertImageToBase64(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Important if the image is from another domain
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);

        const dataURL = canvas.toDataURL('image/png'); // Or 'image/jpeg'
        resolve(dataURL);
      };

      img.onerror = (err) => {
        reject(err);
      };

      img.src = imageUrl;
    });
  }
}
